/**
 * Event Signature Database
 *
 * Maps keccak256 hashes of event signatures to human-readable names.
 * The hash is computed as: keccak256("EventName(param1Type,param2Type,...)")
 *
 * Architecture note: This is a static mapping for common events.
 * To extend to a hybrid approach, add an async lookup function that:
 * 1. Checks this static map first
 * 2. Falls back to an external API (e.g., 4byte.directory, openchain.xyz)
 * 3. Caches results for future lookups
 */

export type EventSignature = {
  /** Human-readable event name */
  name: string;
  /** Full signature (e.g., "Transfer(address,address,uint256)") */
  signature: string;
  /** Parameter names for decoded display */
  params: string[];
};

/**
 * Static mapping of common event signature hashes to their metadata.
 * Hashes should be lowercase for consistent lookup.
 */
export const EVENT_SIGNATURES: Record<string, EventSignature> = {
  // =============================================================================
  // ERC-20 Token Events
  // =============================================================================
  "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef": {
    name: "Transfer",
    signature: "Transfer(address,address,uint256)",
    params: ["from", "to", "value"],
  },
  "0x8c5be1e5ebec7d5bd14f71427d1e84f3dd0314c0f7b2291e5b200ac8c7c3b925": {
    name: "Approval",
    signature: "Approval(address,address,uint256)",
    params: ["owner", "spender", "value"],
  },

  // =============================================================================
  // ERC-721 NFT Events
  // =============================================================================
  // Transfer is same as ERC-20: 0xddf252ad...
  "0x17307eab39ab6107e8899845ad3d59bd9653f200f220920489ca2b5937696c31": {
    name: "ApprovalForAll",
    signature: "ApprovalForAll(address,address,bool)",
    params: ["owner", "operator", "approved"],
  },

  // =============================================================================
  // ERC-1155 Multi-Token Events
  // =============================================================================
  "0xc3d58168c5ae7397731d063d5bbf3d657854427343f4c083240f7aacaa2d0f62": {
    name: "TransferSingle",
    signature: "TransferSingle(address,address,address,uint256,uint256)",
    params: ["operator", "from", "to", "id", "value"],
  },
  "0x4a39dc06d4c0dbc64b70af90fd698a233a518aa5d07e595d983b8c0526c8f7fb": {
    name: "TransferBatch",
    signature: "TransferBatch(address,address,address,uint256[],uint256[])",
    params: ["operator", "from", "to", "ids", "values"],
  },

  // =============================================================================
  // Uniswap V2 Events
  // =============================================================================
  "0xd78ad95fa46c994b6551d0da85fc275fe613ce37657fb8d5e3d130840159d822": {
    name: "Swap",
    signature: "Swap(address,uint256,uint256,uint256,uint256,address)",
    params: [
      "sender",
      "amount0In",
      "amount1In",
      "amount0Out",
      "amount1Out",
      "to",
    ],
  },
  "0x1c411e9a96e071241c2f21f7726b17ae89e3cab4c78be50e062b03a9fffbbad1": {
    name: "Sync",
    signature: "Sync(uint112,uint112)",
    params: ["reserve0", "reserve1"],
  },
  "0x4c209b5fc8ad50758f13e2e1088ba56a560dff690a1c6fef26394f4c03821c4f": {
    name: "Mint",
    signature: "Mint(address,uint256,uint256)",
    params: ["sender", "amount0", "amount1"],
  },
  "0xdccd412f0b1252819cb1fd330b93224ca42612892bb3f4f789976e6d81936496": {
    name: "Burn",
    signature: "Burn(address,uint256,uint256,address)",
    params: ["sender", "amount0", "amount1", "to"],
  },

  // =============================================================================
  // Uniswap V3 Events
  // =============================================================================
  "0xc42079f94a6350d7e6235f29174924f928cc2ac818eb64fed8004e115fbcca67": {
    name: "Swap (V3)",
    signature: "Swap(address,address,int256,int256,uint160,uint128,int24)",
    params: [
      "sender",
      "recipient",
      "amount0",
      "amount1",
      "sqrtPriceX96",
      "liquidity",
      "tick",
    ],
  },
  "0x7a53080ba414158be7ec69b987b5fb7d07dee101fe85488f0853ae16239d0bde": {
    name: "Mint (V3)",
    signature: "Mint(address,address,int24,int24,uint128,uint256,uint256)",
    params: [
      "sender",
      "owner",
      "tickLower",
      "tickUpper",
      "amount",
      "amount0",
      "amount1",
    ],
  },
  "0x0c396cd989a39f4459b5fa1aed6a9a8dcdbc45908acfd67e028cd568da98982c": {
    name: "Burn (V3)",
    signature: "Burn(address,int24,int24,uint128,uint256,uint256)",
    params: ["owner", "tickLower", "tickUpper", "amount", "amount0", "amount1"],
  },
  "0x70935338e69775456a85ddef226c395fb668b63fa0115f5f20610b388e6ca9c0": {
    name: "Collect (V3)",
    signature: "Collect(address,address,int24,int24,uint128,uint128)",
    params: [
      "owner",
      "recipient",
      "tickLower",
      "tickUpper",
      "amount0",
      "amount1",
    ],
  },

  // =============================================================================
  // WETH Events
  // =============================================================================
  "0xe1fffcc4923d04b559f4d29a8bfc6cda04eb5b0d3c460751c2402c5c5cc9109c": {
    name: "Deposit",
    signature: "Deposit(address,uint256)",
    params: ["dst", "wad"],
  },
  "0x7fcf532c15f0a6db0bd6d0e038bea71d30d808c7d98cb3bf7268a95bf5081b65": {
    name: "Withdrawal",
    signature: "Withdrawal(address,uint256)",
    params: ["src", "wad"],
  },

  // =============================================================================
  // Governance Events
  // =============================================================================
  "0xb8e138887d0aa13bab447e82de9d5c1777041ecd21ca36ba824ff1e6c07ddda4": {
    name: "ProposalCreated",
    signature:
      "ProposalCreated(uint256,address,address[],uint256[],string[],bytes[],uint256,uint256,string)",
    params: [
      "proposalId",
      "proposer",
      "targets",
      "values",
      "signatures",
      "calldatas",
      "startBlock",
      "endBlock",
      "description",
    ],
  },
  "0x9a2e42fd6722813d69113e7d0079d3d940171428df7373df9c7f7617cfda2892": {
    name: "VoteCast",
    signature: "VoteCast(address,uint256,uint8,uint256,string)",
    params: ["voter", "proposalId", "support", "weight", "reason"],
  },

  // =============================================================================
  // Aave Events
  // =============================================================================
  "0x2b627736bca15cd5381dcf80b0bf11fd197d01a037c52b927a881a10fb73ba61": {
    name: "Deposit (Aave)",
    signature: "Deposit(address,address,address,uint256,uint16)",
    params: ["reserve", "user", "onBehalfOf", "amount", "referralCode"],
  },
  "0x3115d1449a7b732c986cba18244e897a450f61e1bb8d589cd2e69e6c8924f9f7": {
    name: "Withdraw (Aave)",
    signature: "Withdraw(address,address,address,uint256)",
    params: ["reserve", "user", "to", "amount"],
  },
  "0xc6a898309e823ee50bac64e45ca8adba6690e99e7841c45d754e2a38e9019d9b": {
    name: "Borrow",
    signature: "Borrow(address,address,address,uint256,uint256,uint256,uint16)",
    params: [
      "reserve",
      "user",
      "onBehalfOf",
      "amount",
      "borrowRateMode",
      "borrowRate",
      "referralCode",
    ],
  },
  "0x4cdde6e09bb755c9a5589ebaec640bbfedff1362d4b255ebf8339782b9942faa": {
    name: "Repay",
    signature: "Repay(address,address,address,uint256)",
    params: ["reserve", "user", "repayer", "amount"],
  },

  // =============================================================================
  // Compound Events
  // =============================================================================
  "0x4dec04e750ca11537cabcd8a9eab06494de08da3735bc8871cd41250e190bc04": {
    name: "Mint (Compound)",
    signature: "Mint(address,uint256,uint256)",
    params: ["minter", "mintAmount", "mintTokens"],
  },
  "0xe5b754fb1abb7f01b499791d0b820ae3b6af3424ac1c59768edb53f4ec31a929": {
    name: "Redeem (Compound)",
    signature: "Redeem(address,uint256,uint256)",
    params: ["redeemer", "redeemAmount", "redeemTokens"],
  },

  // =============================================================================
  // Ownership Events
  // =============================================================================
  "0x8be0079c531659141344cd1fd0a4f28419497f9722a3daafe3b4186f6b6457e0": {
    name: "OwnershipTransferred",
    signature: "OwnershipTransferred(address,address)",
    params: ["previousOwner", "newOwner"],
  },

  // =============================================================================
  // Proxy/Upgrade Events
  // =============================================================================
  "0xbc7cd75a20ee27fd9adebab32041f755214dbc6bffa90cc0225b39da2e5c2d3b": {
    name: "Upgraded",
    signature: "Upgraded(address)",
    params: ["implementation"],
  },
  "0x7e644d79422f17c01e4894b5f4f588d331ebfa28653d42ae832dc59e38c9798f": {
    name: "AdminChanged",
    signature: "AdminChanged(address,address)",
    params: ["previousAdmin", "newAdmin"],
  },

  // =============================================================================
  // Chainlink Events
  // =============================================================================
  "0x0559884fd3a460db3073b7fc896cc77986f16e378210ded43186175bf646fc5f": {
    name: "AnswerUpdated",
    signature: "AnswerUpdated(int256,uint256,uint256)",
    params: ["current", "roundId", "updatedAt"],
  },

  // =============================================================================
  // Privacy Protocols - Tornado Cash
  // =============================================================================
  "0xa945e51eec50ab98c161376f0db4cf2aeba3ec92755fe2fcd388bdbbb80ff196": {
    name: "Deposit (Tornado)",
    signature: "Deposit(bytes32,uint32,uint256)",
    params: ["commitment", "leafIndex", "timestamp"],
  },
  "0xe9e508bad6d4c3227e881ca19068f099da81b5164dd6d62b2eaf1e8bc6c34931": {
    name: "Withdrawal (Tornado)",
    signature: "Withdrawal(address,bytes32,address,uint256)",
    params: ["to", "nullifierHash", "relayer", "fee"],
  },

  // =============================================================================
  // Privacy Protocols - Railgun
  // Note: Railgun events use complex struct types, hashes computed from ABI
  // =============================================================================
  "0xc16cde0a1852fb1c89f0ebaae2c67ed37b3db14d5c2ee86cabf1e3c9868c8527": {
    name: "Shield (Railgun)",
    signature: "Shield(uint256,uint256,bytes32[],bytes32[])",
    params: ["treeNumber", "startPosition", "commitments", "ciphertext"],
  },
  "0xbcb7be4569af67940c7fc0a1d72b0eb5f4c12c0fa2e5c1ba7748627a8d29b239": {
    name: "Transact (Railgun)",
    signature: "Transact(uint256,uint256,bytes32[],bytes32[])",
    params: ["treeNumber", "startPosition", "hash", "ciphertext"],
  },
  "0x097486c059f0245c84e80bbdfe4c9dcaa9c1bba1b93da8925ed0f5243d1e5537": {
    name: "Nullifiers (Railgun)",
    signature: "Nullifiers(uint256,bytes32[])",
    params: ["treeNumber", "nullifiers"],
  },
  "0x16ec3c54688e8a19db8c054ee9c479d92ec30c7a4a8e78bc7fbc27fbfac02633": {
    name: "Unshield (Railgun)",
    signature: "Unshield(address,address,uint256,uint256)",
    params: ["to", "token", "amount", "fee"],
  },

  // =============================================================================
  // Privacy Protocols - Aztec (zk.money / Aztec Connect)
  // =============================================================================
  "0x24668a67104793333dd66b090299992839bf498ac0816a0a1d95e38ddf5800a9": {
    name: "DefiBridgeProcessed",
    signature:
      "DefiBridgeProcessed(uint256,uint256,uint256,uint256,uint256,bool,bytes)",
    params: [
      "bridgeId",
      "nonce",
      "totalInputValue",
      "totalOutputValueA",
      "totalOutputValueB",
      "success",
      "errorReason",
    ],
  },
  "0x2f7a885f1b9e27a3e8e6f6c8bfc2b0e58b6eeb9e6c6d0f79f3bc03dc1dd27d9f": {
    name: "RollupProcessed",
    signature: "RollupProcessed(uint256,bytes32[],uint256)",
    params: ["rollupId", "dataStartIndex", "dataSize"],
  },

  // =============================================================================
  // Mixer / Privacy - Generic
  // =============================================================================
  "0x8d1cced004452bd270777a8c670f9f7e7c4fdde1b8d4d53a19f3b9e6c7e8b9c2": {
    name: "Commitment",
    signature: "Commitment(bytes32,uint256,bytes)",
    params: ["commitment", "index", "encryptedOutput"],
  },
  "0x36e69a774ec01f0c9fb53e0e9ec8d2f2c8fa6c3a7d3c4e5e6f7a8b9c0d1e2f3a4": {
    name: "Nullifier",
    signature: "Nullifier(bytes32)",
    params: ["nullifier"],
  },

  // =============================================================================
  // Cross-Chain / Bridge Events
  // =============================================================================
  "0x2849b43074093a05ba3a7cd047df22c8ab8bcb3053e0c6f0f0e3e3d8e7e6f5e4": {
    name: "MessageSent",
    signature: "MessageSent(address,address,uint256,uint256,bytes)",
    params: ["sender", "receiver", "destinationChain", "nonce", "message"],
  },
  "0x6bb7ff708619ba0610cba295a58592e0451dee2622938c8755667688daf3529b": {
    name: "SentMessage",
    signature: "SentMessage(address,address,bytes,uint256,uint256)",
    params: ["target", "sender", "message", "messageNonce", "gasLimit"],
  },
  "0x4641df4a962071e12719d8c8c8e5ac7fc4d97b927346a3d7a335b1f7517e133c": {
    name: "RelayedMessage",
    signature: "RelayedMessage(bytes32)",
    params: ["msgHash"],
  },
};

/**
 * Get event name from a topic0 hash.
 * Returns "Unknown Event" if not found in the static mapping.
 *
 * @param topic0 - The keccak256 hash of the event signature (with 0x prefix)
 * @returns Human-readable event name
 */
export function getEventName(topic0: string | undefined): string {
  if (!topic0) {
    return "Unknown";
  }
  const sig = EVENT_SIGNATURES[topic0.toLowerCase()];
  return sig?.name ?? "Unknown Event";
}

/**
 * Get full event signature info from a topic0 hash.
 * Returns undefined if not found.
 *
 * @param topic0 - The keccak256 hash of the event signature (with 0x prefix)
 * @returns EventSignature object or undefined
 */
export function getEventSignature(
  topic0: string | undefined
): EventSignature | undefined {
  if (!topic0) {
    return;
  }
  return EVENT_SIGNATURES[topic0.toLowerCase()];
}

/**
 * Check if a topic0 hash is a known event signature.
 *
 * @param topic0 - The keccak256 hash of the event signature
 * @returns true if the event is known
 */
export function isKnownEvent(topic0: string | undefined): boolean {
  if (!topic0) {
    return false;
  }
  return topic0.toLowerCase() in EVENT_SIGNATURES;
}

// =============================================================================
// Future: Async lookup with external API fallback
// =============================================================================
//
// To implement hybrid lookup:
//
// const signatureCache = new Map<string, EventSignature>();
//
// export async function getEventNameAsync(topic0: string): Promise<string> {
//   // 1. Check static map
//   const staticResult = getEventName(topic0);
//   if (staticResult !== "Unknown Event") {
//     return staticResult;
//   }
//
//   // 2. Check cache
//   const cached = signatureCache.get(topic0.toLowerCase());
//   if (cached) {
//     return cached.name;
//   }
//
//   // 3. Fetch from external API
//   try {
//     const response = await fetch(
//       `https://api.openchain.xyz/signature-database/v1/lookup?event=${topic0}`
//     );
//     const data = await response.json();
//     if (data.result?.event?.[topic0]?.[0]) {
//       const sig = data.result.event[topic0][0];
//       const eventSig: EventSignature = {
//         name: sig.name.split("(")[0],
//         signature: sig.name,
//         params: [],
//       };
//       signatureCache.set(topic0.toLowerCase(), eventSig);
//       return eventSig.name;
//     }
//   } catch {
//     // Ignore fetch errors, fall through to unknown
//   }
//
//   return "Unknown Event";
// }
